import React from 'react';
import { ApolloError } from '@apollo/client';
import { SessionActions } from '../../account/sessionSlice';
import TeamIconEditor from '../../admin/appearance/TeamIconEditor';
import redirectToHost from '../../admin/appearance/redirectToHost';
import splitSubdomain from '../../admin/appearance/splitSubdomain';
import useUpdateOrganization from '../../admin/appearance/useUpdateOrganization';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { Field } from '../../components/Form';
import { SUBJECT_ORG_ONBOARDING } from '../../constants/onboarding';
import { getUrlWithOnboardingQueries } from '../onboardingQueryString';
import type { OrganizationOnboardingStepBaseProps } from './OrganizationOnboardingStep';
import OrganizationOnboardingStep from './OrganizationOnboardingStep';
import * as styles from './OrganizationOnboardingStepTeam.module.scss';

type Values = { name: string; subdomain: string };

interface OrganizationOnboardingStepTeamProps
  extends OrganizationOnboardingStepBaseProps {
  step: number;
}

const OrganizationOnboardingStepTeam = ({
  step,
  ...props
}: OrganizationOnboardingStepTeamProps) => {
  const organization = useAppSelector((state) => state.session.organization);
  const dispatch = useAppDispatch();
  const updateOrganization = useUpdateOrganization({ host: organization.host });

  const handleSubmit = async (values: Values) => {
    try {
      const { data } = await updateOrganization(values);

      if (
        data?.organization?.host &&
        data?.organization?.host !== document.location.host
      ) {
        redirectToHost(
          data.organization.host,
          () => {
            dispatch(SessionActions.clearSession());
          },
          getUrlWithOnboardingQueries(SUBJECT_ORG_ONBOARDING, step + 1),
        );

        // Return a forever wait to prevent the next step from showing before redirecting
        await new Promise(() => {});
      }
    } catch (errors) {
      if (!(errors instanceof ApolloError) || !errors.graphQLErrors) {
        throw errors;
      }

      const graphQLErrors = errors.graphQLErrors.map((error): typeof error => {
        if (
          error.extensions?.code === 'VALIDATION' &&
          error.extensions.field === 'host'
        ) {
          return {
            ...error,
            extensions: {
              ...error.extensions,
              // The server validates the host field, but we are using the subdomain field in the form.
              // We need to change the field name here so that the Form component can map the error to the correct field.
              field: 'subdomain',
            },
          };
        }
        return error;
      });

      throw new ApolloError({
        ...errors,
        graphQLErrors,
      });
    }
  };

  const [subdomain, baseDomain] = splitSubdomain(organization.host);

  return (
    <OrganizationOnboardingStep<Values>
      {...props}
      initialValues={{ name: organization.name, subdomain: subdomain ?? '' }}
      title="Customize your team"
      description="Enter your team details to reserve a URL"
      onSubmit={handleSubmit}
    >
      <div className={styles.logoEditorContainer}>
        <TeamIconEditor size="large" />
      </div>
      <Field label="Team name" name="name" required />
      <Field
        append={baseDomain}
        label="Subdomain"
        name="subdomain"
        helpText="You can add a custom domain later"
        required
      />
    </OrganizationOnboardingStep>
  );
};

export default OrganizationOnboardingStepTeam;
